var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import moment from 'moment';
import { createStyleSheet, useStyleSheet, Divider, Focusable, Text, View } from '@gopeerproject/ui-kit';
import { ChartFrame } from '../ChartFrame';
import { ColorsShort } from '../../constants';
const from = moment().set('hours', 0);
const hours = new Array(24).fill(0).map((_, i) => ({
    value: 0,
    label: moment(from).add(i, 'hours').format('hA')
}));
export const GridChart = (_a) => {
    var _b;
    var { data = moment.weekdaysShort().map((v) => ({ label: v, value: hours })), total, intervals, units = '', roundTo = 0, height } = _a, baseChartProps = __rest(_a, ["data", "total", "intervals", "units", "roundTo", "height"]);
    const styles = useStyleSheet(getStyles);
    const description = React.useMemo(() => `${total.toFixed(roundTo)}`, [total, roundTo]);
    const tiers = intervals.map((num, index) => {
        var _a;
        return Object.assign(Object.assign({ min: num }, (index + 1 < intervals.length ? { max: intervals[index + 1] } : {})), { label: index + 1 < intervals.length
                ? `${num}-${intervals[index + 1]}`
                : `${num}+`, color: (_a = ColorsShort[index]) !== null && _a !== void 0 ? _a : ColorsShort[0] });
    });
    const getColorForCell = (val) => {
        const result = tiers.find((tier) => val >= tier.min && (!tier.max || val < tier.max));
        return result === null || result === void 0 ? void 0 : result.color;
    };
    return (React.createElement(ChartFrame, Object.assign({ description: description, height: height !== null && height !== void 0 ? height : 420 }, baseChartProps),
        React.createElement(View, { style: styles.grid },
            data.map((row, i) => (React.createElement(View, { key: i, style: [styles.row, { height: 39 }] },
                React.createElement(View, { style: styles.rowLabelContainer },
                    React.createElement(Text, { type: 'body', weight: 'regular', size: 'md', style: styles.rowLabel }, row.label)),
                row.value.map((cell, j) => {
                    const cols = row.value.length;
                    return (React.createElement(Focusable, { key: `${i}-${j}`, style: styles.focusable }, ({ hovered }) => (React.createElement(React.Fragment, null,
                        React.createElement(View, { style: [
                                styles.cell,
                                hovered ? styles.cell__hovered : {},
                                { backgroundColor: getColorForCell(cell.value) }
                            ] }),
                        hovered ? (React.createElement(View, { style: {
                                width: '100%',
                                height: '100%',
                                zIndex: 9,
                                marginTop: '-100%'
                            } },
                            React.createElement(View, { style: [
                                    styles.tooltip,
                                    i < 2
                                        ? styles.tooltip__top
                                        : styles.tooltip__bottom,
                                    j > cols - 2
                                        ? styles.tooltip__left
                                        : j < 1
                                            ? styles.tooltip__right
                                            : {}
                                ] },
                                React.createElement(Text, { type: 'headline', size: 'md' }, cell.value.toFixed(roundTo)),
                                React.createElement(Text, { type: 'body', weight: 'regular', size: 'sm', style: styles.tooltip__label }, units)))) : null))));
                })))),
            React.createElement(View, { style: styles.row },
                React.createElement(View, { style: styles.rowLabelContainer }), (_b = data[0]) === null || _b === void 0 ? void 0 :
                _b.value.map((cell, i) => (React.createElement(View, { key: `axis-${i}`, style: styles.columnLabelContainer },
                    React.createElement(Text, { type: 'body', weight: 'regular', size: 'sm', style: styles.columnLabel }, cell.label)))))),
        React.createElement(Divider, { type: 'horizontal', style: styles.divider }),
        React.createElement(View, { style: styles.legend }, tiers
            .slice()
            // .reverse()
            .map((tier) => (React.createElement(View, { key: tier.label, style: styles.legendItem },
            React.createElement(View, { style: [styles.dot, { backgroundColor: tier.color }] }),
            React.createElement(Text, { type: 'body', weight: 'regular', size: 'sm' }, tier.label)))))));
};
const getStyles = createStyleSheet(({ color, size }) => ({
    grid: {
        flexDirection: 'column',
        gap: size.A,
        marginTop: size.H
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: size.A,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
        // @ts-ignore
        zIndex: 'unset'
    },
    rowLabelContainer: {
        height: '100%',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'stretch'
    },
    rowLabel: {
        color: color.invert2.p56,
        textAlign: 'left',
        width: '100%'
    },
    columnLabelContainer: {
        marginTop: size.B,
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
    },
    focusable: {
        flex: 1,
        height: '100%',
        borderRadius: size.B,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
        // @ts-ignore
        zIndex: 'unset'
    },
    cell: {
        width: '100%',
        height: '100%',
        borderRadius: size.B,
        zIndex: -1
    },
    cell__hovered: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
        // @ts-ignore
        transform: 'scale(.9)'
    },
    tooltip: {
        zIndex: 99,
        backgroundColor: color.bg.v3,
        borderRadius: size.C,
        padding: size.D,
        position: 'absolute',
        margin: 'auto',
        left: '-50%',
        right: '-50%',
        boxShadow: '0px 8px 42px rgba(28, 29, 40, 0.1)'
    },
    tooltip__bottom: {
        bottom: '100%'
    },
    tooltip__top: {
        top: `calc(100% + ${size.A}px)`
    },
    tooltip__label: {
        color: color.invert2.p42
    },
    tooltip__left: {
        left: 'unset',
        right: 0
    },
    tooltip__right: {
        right: 'unset',
        left: 0
    },
    divider: {
        marginVertical: size.G
    },
    legend: {
        flexDirection: 'row',
        gap: size.G
    },
    legendItem: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    dot: {
        width: size.C,
        height: size.C,
        margin: size.C,
        borderRadius: size.C
    }
}));
export default GridChart;
//# sourceMappingURL=index.js.map